/**
 * workflowStructureEvents.ts — Workflow 结构事件类型守卫
 *
 * 职责：
 *     区分 workflow 编排结构 event_type 与 agent loop 事件。
 *
 * 链路位置：
 *     workflowReducer 路由 · hydrateLoopSession 过滤 · Source application_kind 守卫。
 *
 * 当前裁剪范围：
 *     显式集合与 backend sse.WORKFLOW_STRUCTURE_EVENT_TYPES 对齐；
 *     识别逻辑另用前缀（与 task_sync/multi_sse_bridge.py 一致），避免 SDK 新增结构类型漏判。
 */
import { DEFAULT_EVENT_TIERS } from '../types/events';

/** 与 backend/langchain_agentx_stream_ui_backend/task_sync/multi_sse_bridge.py 对齐 */
export const WORKFLOW_STRUCTURE_EVENT_PREFIXES: readonly string[] = [
  'workflow-',
  'stage-',
  'parallel-',
  'route-',
  'subworkflow-',
];

/** 显式 SSOT（文档/契约测试）；新增结构类型应同时满足前缀规则或补入此集合 */
export const WORKFLOW_STRUCTURE_EVENT_TYPES: ReadonlySet<string> = new Set([
  'workflow-start',
  'workflow-end',
  'workflow-failed',
  'subworkflow-start',
  'subworkflow-end',
  'stage-start',
  'stage-done',
  'stage-failed',
  'parallel-item-start',
  'parallel-item-done',
  'parallel-item-failed',
  'parallel-aggregate-start',
  'parallel-aggregate-done',
  'parallel-aggregate-failed',
  'route-branch-start',
  'route-branch-done',
  'route-branch-failed',
]);

/** session reducer（reduceTree）可处理的 event_type 白名单 */
const AGENT_LOOP_REPLAY_EVENT_TYPES: ReadonlySet<string> = new Set(
  Object.keys(DEFAULT_EVENT_TIERS),
);

export function isWorkflowStructureEventType(eventType: string): boolean {
  if (WORKFLOW_STRUCTURE_EVENT_TYPES.has(eventType)) return true;
  return WORKFLOW_STRUCTURE_EVENT_PREFIXES.some((prefix) => eventType.startsWith(prefix));
}

/** agent loop hydrate/replay 允许进入 reduceTree 的事件（正文 + state/debug tier） */
export function isAgentLoopReplayEventType(eventType: string): boolean {
  return AGENT_LOOP_REPLAY_EVENT_TYPES.has(eventType);
}

/**
 * hydrateLoopSession / 宿主 replay：
 * 1. 白名单：只保留 reduceTree 已知 event_type
 * 2. 结构前缀/集合：双重排除 workflow 编排帧（含 session_id 误绑的 dual emission）
 */
export function filterAgentLoopReplayEvents<T extends { event_type: string }>(
  events: T[],
): T[] {
  return events.filter(
    (event) =>
      isAgentLoopReplayEventType(event.event_type)
      && !isWorkflowStructureEventType(event.event_type),
  );
}
